import { useMemo } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';
import { StoreTask } from 'api-marketti/types';

type TaskStatus = 'pending' | 'started' | 'success' | 'failed';
const INCOMPLETE_STATUSES: TaskStatus[] = ['pending', 'started'];

interface TaskStore {
  tasks: Map<string, StoreTask[]>;
}

const initialState: TaskStore = {
  tasks: new Map(),
};

const useTaskStore = create<TaskStore>(() => initialState);

export const updateTasks = (marketId: string, tasks: StoreTask[]) => {
  useTaskStore.setState((state) => {
    const newTasks = new Map(state.tasks.set(marketId, tasks));
    return {
      tasks: newTasks,
    };
  });
};

export const resetTasks = (marketId?: string) => {
  useTaskStore.setState((state) => {
    if (marketId) {
      const newTasks = new Map(state.tasks);
      newTasks.delete(marketId);
      return {
        tasks: newTasks,
      };
    }
    return initialState;
  });
};

export function useTasks() {
  const { tasks } = useTaskStore(
    useShallow((state) => ({
      tasks: state.tasks,
    })),
  );

  const incompleteTasks = useMemo(
    () =>
      Array.from(tasks.values())
        .flat()
        .filter((task) => INCOMPLETE_STATUSES.includes(task.status)),
    [tasks],
  );

  return useMemo(
    () => ({
      tasks,
      incompleteTasks,
    }),
    [tasks, incompleteTasks],
  );
}

export function useMarketTasks(marketId: string) {
  const { tasks } = useTaskStore(
    useShallow((state) => ({
      tasks: state.tasks,
    })),
  );

  const marketTasks = useMemo(() => tasks.get(marketId) ?? [], [tasks, marketId]);
  const incompleteTasks = useMemo(
    () => marketTasks.filter((task) => INCOMPLETE_STATUSES.includes(task.status)),
    [marketTasks],
  );
  const failedTasks = useMemo(
    () => marketTasks.filter((task) => task.status === 'failed'),
    [marketTasks],
  );
  const aggregateState = useMemo(() => getTasksAggregateState(marketTasks), [marketTasks]);

  return useMemo(
    () => ({
      tasks: marketTasks,
      failedTasks,
      incompleteTasks,
      aggregateState,
    }),
    [marketTasks, failedTasks, incompleteTasks, aggregateState],
  );
}

function getTasksAggregateState(tasks: StoreTask[]): 'idle' | 'incomplete' | 'success' | 'failed' {
  if (tasks.length === 0) {
    return 'idle';
  }
  if (tasks.some((task) => INCOMPLETE_STATUSES.includes(task.status as TaskStatus))) {
    return 'incomplete';
  }
  if (tasks.every((task) => task.status === 'success')) {
    return 'success';
  }
  return 'failed';
}
